import { Injectable, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { PayrollStatuses, PayTypes } from '../models/payroll';
import { AuditService } from '../services/audit.service';
import { PayrollService } from '../services/payroll.service';
import {
  manualPayrollPreviewRouteRegex,
  manualPayrollRouteRegex,
  payrollCacheKeyRegex,
  payrollGridRouteRegex,
  payrollPreviewRouteRegex
} from '../utils/regex-patterns';
import { Store } from '@ngxs/store';
import { ClearClientState } from '../state/ClientSelectionState/ClientSelection.state';
import { ClearAuthState } from '../state/AuthenticationState/Authentication.state';

@Injectable({
  providedIn: 'root'
})
export class SignOutToolsComponent implements OnInit {
  constructor(
    private auditService: AuditService,
    private payrollService: PayrollService,
    private router: Router,
    private store: Store
  ) {}

  ngOnInit(): void {}

  // perform any pre-sign out logic necessary
  public preSignOutCleanUp(userId: string): Observable<any> {
    //clear client data from state
    this.store.dispatch(new ClearClientState());
    this.store.dispatch(new ClearAuthState());
    // clear any payroll related session storage data
    for (let key of Object.keys(sessionStorage)) {
      // payroll keys are defined as `payroll-${client}-${year}-${seq}`
      // replicate logic of PayrollManagement clearCachedPayroll
      // NOTE: PayrollManagement not a dependency here to avoid circular dependency
      if (!payrollCacheKeyRegex.test(key)) {
        continue;
      }

      const k = key.split('-');
      const client = k[1];
      const year = +k[2];
      const seq = +k[3];

      sessionStorage.removeItem(`payroll-${client}-${year}-${seq}`);
      sessionStorage.removeItem(`setup-data-${client}-${year}-${seq}`);
      sessionStorage.removeItem(`session-data-${client}-${year}-${seq}`);
      sessionStorage.removeItem(`client-${client}`);
    }

    // if the user is logging out from payroll grid or preview pages
    // set the payroll status to 'Hold'
    if (
      payrollGridRouteRegex.test(this.router.url) ||
      payrollPreviewRouteRegex.test(this.router.url)
    ) {
      // NOTE: ActivatedRoute will not provide params here since a
      // component is not being currently loaded in a router outlet.
      // split the route to get clientCode/year/seqNo
      let clientCode = this.router.url.split('/')[2];
      let year = +this.router.url.split('/')[4];
      let seqNo = +this.router.url.split('/')[5];
      //:client_code/payroll/:year/:seqNo
      if (clientCode == 'payroll' || clientCode == 'payroll-preview') {
        //we are in the client portal and need to shift things one slot to the left.
        clientCode = this.router.url.split('/')[1];
        year = +this.router.url.split('/')[3];
        seqNo = +this.router.url.split('/')[4];
      }

      // FIXME: fetching master payroll object again here,
      // but this can be avoided with a caching solution.
      return this.payrollService
        .getPayroll(clientCode, year, seqNo)
        .pipe(
          concatMap(response => {
            const payroll = response;

            payroll.Status = PayrollStatuses.Hold;

            return this.payrollService
              .updatePayrollStatus(clientCode, payroll.Year, payroll.SequenceNumber, payroll.Status)
              .pipe(map(() => payroll));
          })
        )
        .pipe(
          concatMap(payroll => {
            this.auditService.auditPayroll(`payroll-status-set-to-hold`, {
              ClientCode: payroll.ClientCode,
              PayrollId: payroll.Id,
              Calendars: payroll.Calendars,
              PayDate: payroll.PayDate,
              LocationId: payroll.LocationId,
              SequenceNumber: payroll.SequenceNumber,
              PayrollType: payroll.PayType
            });

            return of(null);
          })
        );
    } else if (
      manualPayrollRouteRegex.test(this.router.url) ||
      manualPayrollPreviewRouteRegex.test(this.router.url)
    ) {
      // NOTE: ActivatedRoute will not provide params here since a
      // component is not being currently loaded in a router outlet.
      // split the route to get clientCode/year/seqNo
      const clientCode = this.router.url.split('/')[2];
      const year = +this.router.url.split('/')[4];
      const seqNo = +this.router.url.split('/')[5];

      return this.payrollService.saveManualPayroll(clientCode, year, seqNo).pipe(
        catchError(err => {
          console.log(err);
          return of(true);
        })
      );
    } else {
      return of(null);
    }
  }
}
